import React, { memo, useCallback, useMemo, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AutoComplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { CircularProgress, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { Modal } from 'ui/components/Modal/Modal';
import { TextField } from 'ui/components/TextField/TextField';
import { MultiFormatInput } from 'ui/components/TextField/MultiFormatInput';
import { useHandleTextFieldChange } from 'services/forms';
import { Errors, validateYup } from 'services/forms/validation';
import {
  Channel,
  ChannelTaxType,
  createTaxRate,
  fetchTaxRates,
  getTaxRates,
  initialTaxRate,
  TaxRate,
  TaxType,
} from 'services/taxRates';
import { activeUserHasPermission } from 'services/user/redux';
import { fetchVendors, getVendors, Vendor } from 'services/vendors';

import { TaxRatesAutocompleteProps, TaxRateVariants } from './types';
import _ from 'lodash';
import { Autocomplete } from '../Autocomplete';
import { formatTypes, roundToDecimals, useGetCurrencySymbol } from 'helpers';
import { useFlags } from 'helpers/useFlags';
import { getSettingsCompany } from 'services/settings/company';
import { InternationalTaxRates } from 'ui/modules/accounting/pages/AccountingSettingsPage/components/TaxRateMappingModal/components/InternationalTaxRates';
import {
  getXeroAccountIsConnected,
  InternationalTaxRate,
} from 'services/integrations/xero';
import { getQuickbooksAccountIsConnected } from 'services/integrations/quickbooks';
import { editTaxRatePermissions } from 'ui/modules/setup/pages/TaxRatesPage/components/helpers';
import { getTaxRateValidationSchema } from 'ui/modules/setup/pages/TaxRatesPage/components/TaxRateDetailsCard/validations';

const filter = createFilterOptions<TaxRate>();

const TaxRatesAutocomplete: React.FC<TaxRatesAutocompleteProps> = (props) => {
  const {
    onChange,
    dataQa,
    permissions = [],
    disabled,
    label,
    value,
    placeholder,
    error,
    required,
    additionalInputProps,
    helperText,
    taxRateVariant = TaxRateVariants.All,
    channelTaxType,
  } = props;

  const dispatch = useDispatch();
  const flags = useFlags();
  const countryCode = useSelector(getSettingsCompany);
  const xeroConnected = useSelector(getXeroAccountIsConnected);
  const qboConnected = useSelector(getQuickbooksAccountIsConnected);

  const { items: taxRates } = useSelector(getTaxRates);
  const { items: vendors } = useSelector(getVendors);
  const currencySymbol = useGetCurrencySymbol();

  const [showModal, setShowModal] = useState(false);
  const [isInternational, setIsInternational] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [selectedValue, setSelectedValue] = useState<number | null>(null);
  const [accountingType, setAccountingType] = useState(Channel.None);

  const [activeTaxRate, setActiveTaxRate] = useState<TaxRate>(initialTaxRate);

  const [formTaxRate, setFormTaxRate] = useState<TaxRate>({
    ...initialTaxRate,
    taxType:
      taxRateVariant === TaxRateVariants.FlatRate
        ? TaxType.flatRate
        : TaxType.percentage,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [, setPercentageValue] = useState(0);
  const editPermissions = editTaxRatePermissions(activeTaxRate);

  const canEdit = useSelector(activeUserHasPermission(permissions));
  const flag = useFlags();
  const isDisabled = disabled || !canEdit;

  useEffect(() => {
    if (countryCode.country !== 'US') setIsInternational(true);
  }, [isInternational]);
  const selectedVendor = useMemo(
    () => vendors.find((v) => v.id === formTaxRate.agencyId) || null,
    [formTaxRate, vendors]
  );

  useEffect(() => {
    setIsInternational(countryCode.country !== 'US');
    if (xeroConnected) {
      setIsConnected(true);
      setAccountingType(Channel.Xero);
    } else if (qboConnected) {
      setIsConnected(true);
      setAccountingType(Channel.Qbo);
    } else {
      setIsConnected(false);
      setAccountingType(Channel.None);
    }
  }, [xeroConnected, qboConnected, countryCode]);
  useEffect(() => {
    if (activeTaxRate.channel) {
      setSelectedTaxRate({
        name: activeTaxRate.mappedTaxName ?? '',
        rate: activeTaxRate.channelTaxRate ?? '',
        code: activeTaxRate.code ?? '',
        taxType: activeTaxRate.channelTaxType ?? null,
      } as unknown as InternationalTaxRate);
    }
  }, [activeTaxRate, isInternational, isConnected, activeTaxRate.id]);
  const [selectedTaxRate, setSelectedTaxRate] =
    useState<InternationalTaxRate | null>(
      activeTaxRate.channel
        ? ({
            name: activeTaxRate.mappedTaxName || '',
            rate: activeTaxRate.channelTaxRate || '',
            code: activeTaxRate.code || '',
            taxType: activeTaxRate.channelTaxType || '',
          } as unknown as InternationalTaxRate)
        : null
    );
  const selectedTaxRate1 = useMemo(
    () => taxRates.find((t) => t.id === value) || null,
    [value, taxRates]
  );

  const percentageOnlyTaxRates = useMemo(
    () => taxRates.filter((tax) => tax.taxType === TaxType.percentage),
    [taxRates]
  );

  const flatOnlyTaxRates = useMemo(
    () => taxRates.filter((tax) => tax.taxType === TaxType.flatRate),
    [taxRates]
  );
  const channelTaxTypeOnlyTaxRates = useMemo(
    () =>
      taxRates.filter(
        (tax) =>
          tax.channelTaxType === channelTaxType ||
          tax.channelTaxType === ChannelTaxType.Both
      ),
    [taxRates]
  );
  useEffect(() => {
    // fetch only if modal is opened
    // Lint skip to be removed
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (showModal) dispatch(fetchVendors());
  }, [dispatch, showModal]);
  const taxRatesType = () => {
    // check what tax rates variant to show
    let rates: TaxRate[] = [];
    if (channelTaxType && flag.intlTaxRates && isConnected && isInternational) {
      rates = channelTaxTypeOnlyTaxRates;
    } else {
      switch (taxRateVariant) {
        case TaxRateVariants.Percentage:
          rates = percentageOnlyTaxRates;
          break;
        case TaxRateVariants.FlatRate:
          rates = flatOnlyTaxRates;
          break;
        default:
          rates = taxRates;
          break;
      }
    }

    if (rates.length === 0) {
      rates.push(initialTaxRate);
    }

    return rates;
  };

  const handleChange = (e: any, taxRate: TaxRate | null) => {
    if (taxRate && (taxRate.id === null || taxRate.id < 0)) {
      setFormTaxRate((old) => ({ ...old, name: taxRate.name }));
      setErrors({});
      setShowModal(true);
      return;
    }

    onChange(taxRate);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormTaxRate(initialTaxRate);
  };

  const validationSchema = useMemo(() => {
    return getTaxRateValidationSchema(
      Boolean(isInternational),
      Boolean(isConnected),
      activeTaxRate.taxType
    );
  }, [activeTaxRate.taxType, isInternational, isConnected]);

  const handleApplyModal = async () => {
    if (!validateYup(formTaxRate, validationSchema, setErrors)) {
      return;
    }

    setIsLoading(true);

    try {
      const newTaxRate = await createTaxRate(formTaxRate);
      // Lint skip to be removed
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      dispatch(fetchTaxRates());
      onChange(newTaxRate);
    } catch {
      setIsLoading(false);
      return;
    }

    setShowModal(false);
    setPercentageValue(0);
    setIsLoading(false);
    setFormTaxRate(initialTaxRate);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormTaxRate,
    formTaxRate
  );

  const handleVendorChange = useCallback(
    (e: React.ChangeEvent<{}>, v: Vendor | null) => {
      setFormTaxRate((old) => ({ ...old, agencyId: v ? v.id : null }));
    },
    [setFormTaxRate]
  );

  const handleTaxChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      const value = event.target.value;

      const percentage = value ? parseFloat(value) / 100 : 0;

      if (formTaxRate.taxType === TaxType.percentage) {
        setFormTaxRate((old) => ({
          ...old,
          percentage: value ? roundToDecimals(percentage, 5) : null,
          amount: 0,
        }));
        setPercentageValue(parseFloat(value));
      } else {
        setFormTaxRate((old) => ({
          ...old,
          amount: value ? parseFloat(value) : null,
          percentage: 0,
        }));
      }
    },
    [formTaxRate]
  );

  const handleTaxTypeChanged = useCallback(
    (e: any) => {
      const taxTypeValue =
        e.target.innerText === currencySymbol
          ? TaxType.flatRate
          : TaxType.percentage;

      setActiveTaxRate((old) => ({
        ...old,
        taxType: taxTypeValue,
        percentage: selectedValue ? selectedValue / 100 : null,
        amount: parseFloat(selectedValue ? selectedValue.toString() : ''),
      }));
    },
    [currencySymbol]
  );
  const handleTaxTypeChange = useCallback(
    (e: any, v: ChannelTaxType | null) => {
      setFormTaxRate((old) => ({
        ...old,
        channelTaxType: v,
      }));
    },
    [setActiveTaxRate]
  );

  const handleInternationalTaxChange = useCallback(
    (taxRate: InternationalTaxRate): boolean => {
      if (!taxRate) {
        return false;
      }
      setSelectedValue(taxRate.rate);
      setFormTaxRate((old) => ({
        ...old,
        mappedTaxName: taxRate.name,
        channel: xeroConnected ? Channel.Xero : Channel.Qbo,

        accountingId: taxRate.code,
        channelTaxType: taxRate.taxType,
      }));

      return true;
    },
    [setActiveTaxRate]
  );
  return (
    <>
      <AutoComplete
        // TODO : RESTYLING : minor style adjustment needed for now until we use the FBOAutoSelect component
        style={{ marginTop: '0px', marginBottom: '0px' }}
        className={'redesign'}
        options={taxRatesType()}
        value={selectedTaxRate1}
        disabled={isDisabled}
        getOptionLabel={(taxRate: TaxRate) => {
          if (!taxRate.id || taxRate.id < 0) {
            if (taxRate.name) {
              return `+ Add "${taxRate.name}"`;
            } else {
              return `+ Create New`;
            }
          }

          return taxRate.name || '';
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          if (!!params.inputValue) {
            filtered.unshift({
              ...initialTaxRate,
              name: params.inputValue || null,
            });
          }

          return filtered;
        }}
        onChange={handleChange}
        autoSelect={false}
        renderInput={(params) => {
          // params.InputProps.endAdornment is component with 2 children
          // first child is clear button, so we want to set it to null
          const endAdornment = params.InputProps.endAdornment as any;

          return (
            <TextField
              className={'redesign'}
              // we are omitting props that would overwrite our styling in TextField
              {..._.omit(params, 'variant', 'size', 'InputLabelProps')}
              label={label}
              placeholder={placeholder}
              error={error}
              required={required}
              helperText={helperText}
              InputProps={{
                ...params.InputProps,
                ...additionalInputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {endAdornment}
                  </React.Fragment>
                ),
              }}
              inputProps={{
                'data-qa': dataQa,
                ...params.inputProps,
              }}
            />
          );
        }}
        clearIcon={
          dataQa && (
            <CloseIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-clear-icon`}
            />
          )
        }
        popupIcon={
          dataQa && (
            <ArrowDropDownIcon
              fontSize="small"
              data-qa={`${dataQa}-autocomplete-dropdown-icon`}
            />
          )
        }
      />
      <Modal
        open={showModal}
        title="New Tax Rate"
        onCancelClicked={handleCloseModal}
        onApplyClicked={handleApplyModal}
        isLoadingContent={isLoading}
      >
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              className="redesign"
              variant="standard"
              name="name"
              label="Name"
              value={formTaxRate.name}
              onChange={handleTextFieldChange}
              required
              error={!!errors.name}
              helperText={errors.name}
              placeholder="Enter name"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              className="redesign"
              variant="standard"
              name="code"
              label="Code"
              value={formTaxRate.code}
              onChange={handleTextFieldChange}
              required
              error={!!errors.code}
              helperText={errors.code}
              placeholder="Enter code"
            />
          </Grid>
          {isInternational && isConnected && flags.intlTaxRates && (
            <Grid item xs={6}>
              <TextField
                className="redesign"
                variant="standard"
                value={formTaxRate.channelTaxType}
                disabled={isInternational && isConnected}
                label="Type"
              />
            </Grid>
          )}

          {isInternational && !isConnected && flags.intlTaxRates && (
            <Grid item xs={6}>
              <Autocomplete
                options={[
                  ChannelTaxType.Purchase,
                  ChannelTaxType.Sales,
                  ChannelTaxType.Both,
                ]}
                name="type"
                label="Type"
                value={formTaxRate.channelTaxType}
                onChange={handleTaxTypeChange}
                dataQa="intil-tax-type"
                required
                error={!!errors.channelTaxType}
                helperText={errors.channelTaxType}
                placeholder="Select type"
              />
            </Grid>
          )}
          <Grid item xs={isInternational && flags.intlTaxRates ? 6 : 12}>
            <Autocomplete
              onChange={handleVendorChange}
              value={selectedVendor}
              label="Vendor"
              options={vendors}
              getOptionLabel={(option) => option.name}
              required
              error={!!errors.agencyId}
              helperText={errors.agencyId}
              placeholder="Select vendor"
            />
          </Grid>
          <Grid item xs={6}>
            <MultiFormatInput
              className="redesign"
              variant="standard"
              type="text"
              label="Amount"
              placeholder="Enter amount"
              value={selectedValue}
              onFormatChange={handleTaxTypeChanged}
              onChange={handleTaxChange}
              permissions={editPermissions}
              options={formatTypes(currencySymbol)}
              decimalPlaces={
                activeTaxRate.taxType === TaxType.percentage ? 3 : 2
              }
              error={
                activeTaxRate.taxType === TaxType.percentage
                  ? !!errors.percentage
                  : !!errors.amount
              }
              helperText={
                activeTaxRate.taxType === TaxType.percentage
                  ? errors.percentage
                  : errors.amount
              }
              activeIndex={activeTaxRate.taxType === TaxType.percentage ? 0 : 1}
              required
              dataQa="tax-rate-amount"
              disabled={isInternational && isConnected && flags.intlTaxRates}
            />
          </Grid>
          <Grid item xs={6} style={{ marginTop: '16px' }}>
            {isInternational && isConnected && flags.intlTaxRates && (
              <InternationalTaxRates
                accountingType={accountingType}
                label={xeroConnected ? 'Xero Mapping' : 'QuickBooks Mapping'}
                selectedOptionCallback={handleInternationalTaxChange}
                // @ts-ignore
                selectedValue={selectedTaxRate}
                dataQa="intil-tax-rate"
                disabled={false}
                setSelectedValue={setSelectedTaxRate}
                activeTaxRateId={activeTaxRate.id}
                error={!!errors.mappedTaxName}
                helperText={errors.mappedTaxName}
              />
            )}
          </Grid>
        </Grid>
      </Modal>
    </>
  );
};

export default memo(TaxRatesAutocomplete);
